import { ExpandMore } from '@mui/icons-material';
import {
	Box,
	ButtonBase,
	Fade,
	Grid2,
	Skeleton,
	Stack,
	Typography,
	useTheme
} from '@mui/material';
import IconTransition from 'components/shared/IconTransition';
import { Method } from 'consts';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IIdP } from 'types/api';
import { isSubStrI } from 'utils/string';
import IdpCard, { CARD_HEIGHT, CARD_WIDTH } from './IdPCard';

interface IIdpGalleryProps {
	loader?: boolean;
	filterText?: string;
	idps: IIdP[];
	selectedIdP?: string | null;
	selectedMethod?: Method | null;
	onMethodClick?: (idpID: string, method: Method) => any;
	onIdPCardClick?: (idpID: string) => any;
	disabled?: boolean;
}

const NUM_ROWS_COLLAPSED = 2;
const GRID_SPACING = 3;

const IdpGallery: React.FC<IIdpGalleryProps> = ({
	idps,
	loader = false,
	filterText = '',
	selectedIdP = null,
	selectedMethod = null,
	onMethodClick = undefined,
	onIdPCardClick = undefined,
	disabled = false
}) => {
	const galleryCardOverflowContainerRef = useRef<HTMLDivElement>();
	const galleryCollapseButton = useRef<HTMLButtonElement>(null);

	const [innerSelectedIdP, setInnerSelectedIdP] = useState<string | null>(
		selectedIdP
	);

	const [open, setOpen] = useState(false);

	const { spacing } = useTheme();

	const visibleIdPs = useMemo(
		() => idps.filter((idp) => isSubStrI(idp.name, filterText)),
		[idps, filterText]
	);

	/* istanbul ignore next: cannot test this properly  */
	useLayoutEffect(() => {
		const container = galleryCardOverflowContainerRef.current;
		const collapseButton = galleryCollapseButton.current;

		if (!container || !collapseButton) {
			return;
		}

		// Measured from the DOM, not from ref callbacks: those only fire on mount,
		// so the row count would not follow a search. The test id excludes the
		// loading skeletons.
		const cards = Array.from(
			container.querySelectorAll<HTMLElement>(
				'[data-testid="idp-gallery-card"]'
			)
		);
		const rowTops = Array.from(
			new Set(cards.map((card) => card.offsetTop))
		).sort((a, b) => a - b);
		// offsetHeight, not clientHeight: the cards are border-box, so clientHeight
		// would drop their 1px borders and clip each row.
		const cardHeight = cards[0]?.offsetHeight || 0;

		// No matches: nothing to measure, so close and hide the control. Returning
		// before the heights below keeps the last measured size while typing.
		if (cards.length === 0) {
			setOpen(false);
			collapseButton.style.display = 'none';
			return;
		}

		if (!cardHeight) {
			return;
		}

		// Taken out of the layout rather than disabled, so it leaves no gap above
		// the divider when every row already fits.
		const hasHiddenRows = rowTops.length > NUM_ROWS_COLLAPSED;

		if (!hasHiddenRows) {
			setOpen(false);
		}

		collapseButton.style.display = hasHiddenRows ? '' : 'none';

		const lastVisibleRow = open
			? rowTops.length - 1
			: Math.min(NUM_ROWS_COLLAPSED, rowTops.length) - 1;

		// Nominal two rows, so a narrowing search does not shift the page.
		container.style.minHeight = `calc(${NUM_ROWS_COLLAPSED * cardHeight}px + ${NUM_ROWS_COLLAPSED - 1} * ${spacing(GRID_SPACING)})`;
		// Clipped to a measured row bottom, so the grid gutter is never re-derived.
		container.style.maxHeight = `${rowTops[lastVisibleRow] + cardHeight}px`;
	}, [open, visibleIdPs, spacing]);

	return (
		<Stack gap={3} data-testid="idps-gallery">
			<Box
				position="relative"
				ref={galleryCardOverflowContainerRef}
				overflow="hidden"
				sx={{
					transition: 'max-height 300ms ease-in-out'
				}}
			>
				{loader ? (
					<Grid2 container spacing={GRID_SPACING}>
						{Array(20)
							.fill(1)
							.map((_, i) => (
								<Fade
									// eslint-disable-next-line react/no-array-index-key
									key={i}
									in
									timeout={500}
									style={{ transitionDelay: `${50 * i}ms` }}
								>
									<Skeleton
										variant="rectangular"
										width={CARD_WIDTH}
										height={CARD_HEIGHT}
										animation="wave"
									/>
								</Fade>
							))}
					</Grid2>
				) : (
					<Fade key={2} in timeout={500}>
						<Grid2 container spacing={GRID_SPACING}>
							{visibleIdPs.map((idp) => (
								<IdpCard
									idp={idp}
									onClick={() => {
										onIdPCardClick?.(idp.id);
										setInnerSelectedIdP(idp.id);
									}}
									disabled={disabled}
									selected={innerSelectedIdP === idp.id}
									showMethods={!onIdPCardClick && innerSelectedIdP === idp.id}
									onMethodClick={(method) => {
										onMethodClick?.(idp.id, method);
									}}
									selectedMethod={
										selectedIdP === idp.id ? selectedMethod : null
									}
									key={idp.id}
								/>
							))}
						</Grid2>
					</Fade>
				)}
			</Box>
			<ButtonBase
				ref={galleryCollapseButton}
				data-testid="gallery-collapse-button"
				disableRipple
				onClick={() => setOpen(!open)}
			>
				<Typography>
					{intl.tr(
						open
							? 'ssoConfig.guide.selectIdP.gallery.showLess'
							: 'ssoConfig.guide.selectIdP.gallery.showMore'
					)}
				</Typography>
				<IconTransition
					transitionProperty="transform"
					apply={open}
					to="rotateX(180deg)"
				>
					<ExpandMore color="action" />
				</IconTransition>
			</ButtonBase>
		</Stack>
	);
};

export default IdpGallery;
